import {
  FieldText,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import { NotificationPayloadT } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { ChangeEvent, FormEvent, PureComponent } from 'react';

import { TeamPayload } from 'hocs/teams/api/types';
import { getValidateNameFn } from 'utils/formValidator';

import styles from './teamsCreateModal.module.scss';

export type TeamsCreateModalOwnProps = {
  isOpen: boolean;
  onClose: (refetchData?: boolean) => void;
  createTeam: (team: TeamPayload) => Promise<unknown>;
};

export type TeamsCreateModalDispatchProps = {
  notificationAdd: (payload: NotificationPayloadT) => void;
};

export type TeamsCreateModalStateProps = {};

type FormErrors = {
  name: string;
};
export type TeamsCreateModalState = {
  formValues: {
    name: string;
  };
  formErrors: FormErrors;
};

type TeamsCreateModalProps = TeamsCreateModalOwnProps &
  TeamsCreateModalDispatchProps &
  TeamsCreateModalStateProps;

export class TeamsCreateModalView extends PureComponent<
  TeamsCreateModalProps,
  TeamsCreateModalState
> {
  static getInitialState = (): TeamsCreateModalState => ({
    formErrors: { name: '' },
    formValues: { name: '' },
  });

  state = TeamsCreateModalView.getInitialState();

  @boundMethod
  getFormErrors(): FormErrors {
    const { formValues } = this.state;
    const formErrors: FormErrors =
      TeamsCreateModalView.getInitialState().formErrors;

    const errorMessage = getValidateNameFn({
      requiredOptions: { errorMessage: 'Team name is required' },
    })(formValues.name);
    if (errorMessage) {
      formErrors.name = errorMessage;
    }

    return formErrors;
  }

  @boundMethod
  updateFormErrors(): void {
    this.setState({ formErrors: this.getFormErrors() });
  }

  isFormValid(): boolean {
    const formErrors = this.getFormErrors();
    return !formErrors.name;
  }

  @boundMethod
  handleNameChange(e?: ChangeEvent<HTMLInputElement>) {
    if (!e) return;
    this.setState(
      {
        formValues: {
          name: e.target.value,
        },
      },
      () => {
        this.updateFormErrors();
      },
    );
  }

  @boundMethod
  handleSubmit(e?: FormEvent) {
    e?.preventDefault();

    if (!this.isFormValid()) {
      return;
    }

    const { formValues } = this.state;
    const { createTeam, notificationAdd } = this.props;

    ampli.settingsTeamModalFormSubmitted({ teamName: formValues.name });

    createTeam(formValues)
      .then(() => {
        const notificationOptions = {
          title: 'Team created',
          text: `"${formValues.name}" team has been created`,
          icon: 'i-done',
          className: 'success',
          time: 5000,
        };

        notificationAdd(notificationOptions);
        this.handleClose();
      })
      .catch((e) => {
        const notificationOptions = {
          title: 'An error occurred',
          text: `An error occurred when trying to create the "${formValues.name}" team`,
          icon: 'i-exclamation',
          className: 'danger',
        };

        notificationAdd(notificationOptions);
        throw new Error('Team already exist');
      });
  }

  @boundMethod
  handleClose(refetchData = true) {
    this.setState(TeamsCreateModalView.getInitialState());
    this.props.onClose(refetchData);
  }

  render() {
    const { isOpen } = this.props;
    const { formValues, formErrors } = this.state;

    return (
      <Modal
        isOpen={isOpen}
        onClose={() => this.handleClose(false)}
        className={styles.root}
      >
        <ModalHead
          title="Create team"
          onClose={() => this.handleClose(false)}
        />
        <form onSubmit={this.handleSubmit}>
          <ModalBody className={styles.teamModalBody}>
            <FieldText
              label="Team name"
              required
              placeholder="Team name..."
              meta={{ error: formErrors.name }}
              input={{
                name: 'teamName',
                value: formValues.name,
                onChange: this.handleNameChange,
                onBlur: this.updateFormErrors,
              }}
            />
          </ModalBody>
          <ModalFoot
            cancelOnClick={() => this.handleClose(false)}
            cancelName="Cancel"
            confirmOnClick={this.handleSubmit}
            confirmName="Save"
            confirmProps={{
              disabled: !this.isFormValid(),
            }}
          />
        </form>
      </Modal>
    );
  }
}
